import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { format, isBefore, parse, startOfDay, subDays } from 'date-fns';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { Link as ReactLink } from 'react-router-dom';
import { Bar, BarChart, Label, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { DEFAULT_CREDIT_USAGE_HISTORY_DAYS, useSatelliteCreditUsageStats } from '@/api/credits';
import { SubscriptionInfoItem, useSubscriptionsInfo } from '@/api/subscriptions';
import { useCreditBalance } from '@/api/userAccount';
import { AlertChip } from '@/components/AlertChip';
import { Timestamp } from '@/components/data/Timestamp';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { InfoTip } from '@/components/ui/InfoTip';
import { RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY } from '@/config';
import { useNow } from '@/hooks/useNow';
import { useTzContext } from '@/hooks/useTzContext';
import theme, { minorTextGrey } from '@/theme';
import { epochToDate, pickColors } from '@/utils';

const NO_DATA_PLACEHOLDER_SPOTTER_ID = 'placeholder';
/**
 * If anyone has this many credits, it's because we manually set it so high they would never, ever run out.
 */
const INFINITE_CREDITS_THRESHOLD = 100000000;

type UsageBreakdownChartProps = {
    usageBreakdown: Record<string, string | number>[];
    spotterids: string[];
    averageDailyUsage?: number;
};

const UsageBreakdownChart = ({ usageBreakdown, spotterids, averageDailyUsage }: UsageBreakdownChartProps) => {
    // At least one spotter id should be there to render tooltip
    spotterids.push(NO_DATA_PLACEHOLDER_SPOTTER_ID);
    const barColors = useMemo(() => pickColors(spotterids), [spotterids]);
    const hasData = useMemo(() => {
        return usageBreakdown.some((day) => spotterids.some((id) => Number(day[id]) > 0));
    }, [usageBreakdown, spotterids]);
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flex: 1,
                justifyContent: 'space-between',
                svg: {
                    outline: 'none',
                    g: {
                        outline: 'none',
                    },
                },
            }}
        >
            <Typography variant="button" color={minorTextGrey} sx={{ ml: 2 }}>
                {!!averageDailyUsage && (
                    <>
                        Current average consumption:{' '}
                        {averageDailyUsage.toLocaleString('en-US', { maximumFractionDigits: 0 })} credits per day
                        <InfoTip iconSize="inherit">
                            Average consumption by currently enabled Spotters over the past{' '}
                            {DEFAULT_CREDIT_USAGE_HISTORY_DAYS} days, or since they were most recently enabled
                        </InfoTip>
                    </>
                )}
            </Typography>
            <ResponsiveContainer width="100%" minHeight={150}>
                <BarChart
                    data={usageBreakdown}
                    margin={{
                        right: 30,
                        left: 16,
                        bottom: 5,
                    }}
                >
                    <XAxis
                        dataKey="xAxisLabel"
                        style={{
                            fontFamily: theme.typography.fontFamily,
                        }}
                    />
                    <YAxis
                        width="auto"
                        style={{
                            fontFamily: theme.typography.fontFamily,
                        }}
                        tickFormatter={(value) => (hasData ? value?.toLocaleString('en-US') : '')}
                        tickLine={false}
                    />
                    <Tooltip
                        formatter={(value: number | undefined, name: string | undefined) => {
                            if (value === undefined || value === 0) {
                                return ['No credits used on this day'];
                            }
                            return [value.toLocaleString('en-US'), name ?? ''];
                        }}
                        itemSorter={(item) => {
                            const index = spotterids.indexOf(`${item.name}`);
                            const reverseOrderIndex = spotterids.length - index;
                            return reverseOrderIndex;
                        }}
                        contentStyle={{
                            fontFamily: theme.typography.fontFamily,
                        }}
                        labelFormatter={(_, datapoint) => {
                            return datapoint?.[0]?.payload?.tooltipLabel;
                        }}
                    />
                    {!hasData && (
                        <>
                            <Label style={{ fontFamily: theme.typography.fontFamily }} position="center">
                                No recent history
                            </Label>
                            <Bar dataKey={NO_DATA_PLACEHOLDER_SPOTTER_ID} fill="transparent" />
                        </>
                    )}
                    {spotterids.map((id, i) => (
                        <Bar
                            key={id}
                            dataKey={id}
                            stackId="a"
                            fill={barColors[i]}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            shape={(props: Record<string, any>) => {
                                const { x, y, width, height, value } = props;
                                const isNoData = value[0] === value[1];

                                return (
                                    <g>
                                        <rect x={x} y={y} width={width} height={height} fill={barColors[i]} />

                                        {!isNoData && (
                                            <line
                                                x1={x}
                                                y1={y}
                                                x2={x + width}
                                                y2={y}
                                                stroke="#ffffff"
                                                strokeWidth={0.75}
                                            />
                                        )}
                                    </g>
                                );
                            }}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};

type SatelliteCreditsInfoSectionProps = {
    subscriptionData: SubscriptionInfoItem[];
    creditBalance: number;
    runOutOfCreditsDate?: Date | null;
};

export const SatelliteCreditsInfoSection = ({
    subscriptionData,
    creditBalance,
    runOutOfCreditsDate,
}: SatelliteCreditsInfoSectionProps) => {
    const hasSubscription = !!subscriptionData.length;
    const isPastDue = hasSubscription && subscriptionData[0].status === 'past_due';

    const recurringCreditsItem = subscriptionData[0]?.items.find(
        (item) => item.price_lookup_key === RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY
    );
    const hasRecurringCredits = !!recurringCreditsItem;
    const recurringCreditsAmount = recurringCreditsItem?.quantity ?? 0;
    const endOfCurrentSubscriptionPeriodDate = subscriptionData[0]?.current_period_end;
    const hasInfiniteCredits = creditBalance >= INFINITE_CREDITS_THRESHOLD;
    const hasPositiveBalance = creditBalance > 0;
    const showNoCreditsMessage = hasSubscription && !hasPositiveBalance;
    const showBaseCreditsMessage = hasSubscription && hasPositiveBalance;
    /*
        Show run out of credits date if the sub does not have recurring credits
        or if recurring credits won't arrive before the run out date
     */
    const showRunOutOfCreditsMessage =
        !hasInfiniteCredits &&
        !isNil(runOutOfCreditsDate) &&
        (!hasRecurringCredits ||
            (hasRecurringCredits && isBefore(runOutOfCreditsDate, epochToDate(endOfCurrentSubscriptionPeriodDate))));
    const showNoRecentUsageMessage = !hasInfiniteCredits && isNil(runOutOfCreditsDate);

    return (
        <>
            <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Stack direction="row" gap={0.5} alignItems="center" data-testid="credit-balance-text">
                        <Typography variant="h5">Total satellite credits: </Typography>
                        {hasInfiniteCredits ? (
                            <Typography variant="h3" component="span">
                                &infin;
                            </Typography>
                        ) : (
                            <Typography variant="h5">{creditBalance.toLocaleString('en-US')}</Typography>
                        )}
                    </Stack>
                    {!hasPositiveBalance && <AlertChip severity="error" label="No credits" />}
                </Stack>
                <Stack>
                    {showNoCreditsMessage && (
                        <Typography variant="body2" gutterBottom data-testid="no-credits-message">
                            You have no satellite credits. Devices using satellite telemetry will not be able to
                            transmit data until you add credits.
                        </Typography>
                    )}

                    {showBaseCreditsMessage && (
                        <Typography variant="body2" gutterBottom data-testid="base-credits-message">
                            Satellite credits are required to transmit data via satellite telemetry.
                            {showRunOutOfCreditsMessage && (
                                <span data-testid="projected-run-out-date-message">
                                    {' '}
                                    Based on current use, you will run out on{' '}
                                    <strong>
                                        <Timestamp value={runOutOfCreditsDate} unit="date" type="medium" />
                                    </strong>
                                    .
                                </span>
                            )}
                            {showNoRecentUsageMessage && (
                                <span data-testid="no-recent-usage-message">
                                    {' '}
                                    Burn down estimate not available because no Spotters were active in the past{' '}
                                    {DEFAULT_CREDIT_USAGE_HISTORY_DAYS} days.
                                </span>
                            )}
                        </Typography>
                    )}

                    {hasRecurringCredits && (
                        <Typography variant="body2" gutterBottom data-testid="recurring-credits-message">
                            {recurringCreditsAmount} recurring credits will be added on{' '}
                            <strong>
                                <Timestamp value={endOfCurrentSubscriptionPeriodDate} unit="date" type="medium" />
                            </strong>
                            .
                        </Typography>
                    )}

                    {!hasSubscription && (
                        <Typography variant="body2" gutterBottom data-testid="no-subscription-message">
                            Satellite credits are required to transmit data via satellite telemetry. Burn down estimate
                            not available because you do not have an active subscription. Enable device telemetry to
                            start a new subscription.
                        </Typography>
                    )}
                </Stack>
            </Stack>
            <Stack direction="row" spacing={2} mb={2}>
                <Button
                    component={ReactLink}
                    variant="outlined"
                    to="/payment/satelliteCredits/onetime"
                    state={{ transactionBackTarget: 'telemetry' }}
                    data-testid="add-one-time-credits-button"
                >
                    Add one-time credits
                </Button>
                {hasSubscription && (
                    <Button
                        component={ReactLink}
                        disabled={isPastDue}
                        variant="contained"
                        to="/payment/satelliteCredits/recurring"
                        state={{ transactionBackTarget: 'telemetry' }}
                        data-testid="recurring-credits-button"
                    >
                        {hasRecurringCredits ? 'Change recurring credits' : 'Set up recurring credits'}
                    </Button>
                )}
            </Stack>
        </>
    );
};

export const CreditsSummarySection = () => {
    const MAX_CHART_ENTRIES = 6;
    const tzContext = useTzContext();
    const now = useNow(3600);
    const { data: balance, isPending: isBalancePending } = useCreditBalance();
    const { data: subscriptionInfo, isPending: isSubscriptionInfoPending } = useSubscriptionsInfo();
    const {
        data: { runOutOfCreditsEstimate, averageDailyUsageByActiveSpotters, usageHistory, historyDays } = {},
        isPending: isUsagePending,
    } = useSatelliteCreditUsageStats();

    const { usageBreakdown, spotterids } = useMemo(() => {
        if (!usageHistory) {
            return { usageBreakdown: [], spotterids: [] };
        }

        // Total consumption per spotter for the entire period
        const spotterTotals: Record<string, number> = {};
        usageHistory.forEach((item) => {
            item.consumption_breakdown.forEach((consumption) => {
                spotterTotals[consumption.spotter_id] =
                    (spotterTotals[consumption.spotter_id] || 0) + consumption.amount;
            });
        });

        const topConsumersSpotterIds = Object.entries(spotterTotals)
            .sort(([, totalA], [, totalB]) => totalB - totalA)
            .map(([spotterId]) => spotterId);

        /*
            MAX_CHART_ENTRIES, which includes the named spotters + "Other" category
            splicing the spotterIds to MAX_CHART_ENTRIES - 1 to account for "Other"
         */
        if (topConsumersSpotterIds.length > MAX_CHART_ENTRIES) {
            topConsumersSpotterIds.splice(MAX_CHART_ENTRIES - 1);
        }

        const existingDates: { [transactionDate: string]: { [key: string]: number | string } } = {};
        usageHistory.forEach((item) => {
            const transactionTs = parse(item.transaction_date, 'yyyy-MM-dd', startOfDay(now, tzContext), tzContext);
            const tooltipLabel = format(transactionTs, 'PPP', tzContext);
            const tagsConsumption: Record<string, string | number> = {
                tooltipLabel,
                xAxisLabel: format(transactionTs, 'LLL d', tzContext),
                ts: transactionTs.getTime(),
            };

            let otherTotal = 0;

            item.consumption_breakdown.forEach((consumption) => {
                if (topConsumersSpotterIds.includes(consumption.spotter_id)) {
                    tagsConsumption[consumption.spotter_id] = consumption.amount;
                } else {
                    otherTotal += consumption.amount;
                }
            });

            if (otherTotal > 0) {
                tagsConsumption['Other'] = otherTotal;
            }

            existingDates[tooltipLabel] = tagsConsumption;
        });

        const usageBreakdown: Record<string, string | number>[] = [];
        for (let i = 0; i <= historyDays!; i++) {
            const currentDate = subDays(now, i, tzContext);
            const tooltipLabel = format(currentDate, 'PPP', tzContext);
            const existingData = existingDates[tooltipLabel];

            if (existingData) {
                // Use existing data
                usageBreakdown.push(existingData);
            } else {
                // Create empty data for missing day
                const emptyDayData: Record<string, string | number> = {
                    tooltipLabel,
                    xAxisLabel: format(currentDate, 'LLL d', tzContext),
                    ts: currentDate.getTime(),
                    /*
                        For the tooltip to be shown, at least one key matching the legend items is required
                        For no data case we just add a placeholder spotter id with 0 consumption so the y axis is shown
                     */
                    [topConsumersSpotterIds[0] ?? NO_DATA_PLACEHOLDER_SPOTTER_ID]: 0,
                };

                usageBreakdown.push(emptyDayData);
            }
        }

        // Sort by timestamp ascending
        usageBreakdown.sort((a, b) => Number(a.ts) - Number(b.ts));
        const spotterids = [...topConsumersSpotterIds];
        // sort spotterids in the order of consumption
        spotterids.sort((a, b) => (spotterTotals[b] || 0) - (spotterTotals[a] || 0));
        if (Object.keys(spotterTotals).length > MAX_CHART_ENTRIES) {
            // Other category is always at the bottom of the chart
            spotterids.unshift('Other');
        }

        return {
            usageBreakdown,
            spotterids,
        };
    }, [historyDays, now, tzContext, usageHistory]);

    if (isBalancePending || isUsagePending || isSubscriptionInfoPending) {
        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                        <GenericSkeleton sx={{ width: '100%' }} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                        <GenericSkeleton sx={{ width: '100%' }} />
                    </Grid>
                </Grid>
            </Box>
        );
    }

    return (
        <Paper square variant="outlined">
            <Grid container spacing={2} sx={{ p: 3 }}>
                <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'space-between' }}
                >
                    <SatelliteCreditsInfoSection
                        subscriptionData={subscriptionInfo?.data ?? []}
                        creditBalance={balance?.data?.credit_balance ?? 0}
                        runOutOfCreditsDate={runOutOfCreditsEstimate}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                    <UsageBreakdownChart
                        usageBreakdown={usageBreakdown}
                        spotterids={spotterids}
                        averageDailyUsage={averageDailyUsageByActiveSpotters}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};
